/**
 * BulkTaskActionsBar
 * Floating action bar shown when one or more tasks are selected.
 */

import React from 'react';
import { Button, Typography, CircularProgress, Paper } from '@mui/material';
import { Trash2, X } from 'lucide-react';

interface BulkTaskActionsBarProps {
  selectedCount: number;
  isDeleting?: boolean;
  onClear: () => void;
  onDelete: () => void;
}

const BulkTaskActionsBar: React.FC<BulkTaskActionsBarProps> = ({
  selectedCount,
  isDeleting = false,
  onClear,
  onDelete,
}) => {
  if (selectedCount <= 0) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: { xs: 88, md: 28 },
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.25,
        borderRadius: 999,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        minWidth: { xs: 'calc(100% - 32px)', sm: 'auto' },
        maxWidth: 'calc(100% - 32px)',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
        {selectedCount} selected
      </Typography>
      <Button
        size="small"
        color="inherit"
        onClick={onClear}
        disabled={isDeleting}
        startIcon={<X size={14} />}
        sx={{ textTransform: 'none' }}
      >
        Clear
      </Button>
      <Button
        size="small"
        variant="contained"
        color="error"
        onClick={onDelete}
        disabled={isDeleting}
        startIcon={
          isDeleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={14} />
        }
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        {isDeleting
          ? 'Deleting...'
          : `Delete ${selectedCount} task${selectedCount === 1 ? '' : 's'}`}
      </Button>
    </Paper>
  );
};

export default BulkTaskActionsBar;
